import React, { useEffect, useState } from 'react';
import {
  Get,
  Patch,
  Post,
  Delete as DeleteReq,
} from '../../Axios/AxiosFunctions';
import { useSelector } from 'react-redux';
import {
  TextField,
  MenuItem,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  CircularProgress,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { rolesOptions } from '../../constant/commonData';
import LeadsTable from './LeadsTable';
import { apiUrl } from '../../config/apiUrl';

export default function PhoneNumbersOnlyTable({ onPhoneCountUpdate }) {
  const { access_token: accessToken, company_id: companyId } = useSelector(
    (state) => state?.authReducer || {},
  );
  const [leadsData, setLeadsData] = useState([]);
  const [customLeads, setCustomLeads] = useState([]);
  const [actionLoading, setActionLoading] = useState(new Set());
  const [tableLoading, setTableLoading] = useState(true);
  const [showPhoneEdit, setShowPhoneEdit] = useState(false);
  const [phoneEditData, setPhoneEditData] = useState({
    oldPhone: '',
    newPhone: '',
    firstName: '',
    lastName: '',
    email: '',
    address: '',
    role: '',
  });
  const [ contactsPhones, setContactsPhones ] = useState( [] );
  
  

  // Validate if a phone number is a valid US/CA number (10+ digits, starts with +1 or 1)
  const isValidUsCaNumber = (phoneNumber) => {
    if (!phoneNumber) return false;

    // Remove all non-digit characters
    const digits = phoneNumber.replace(/\D/g, '');

    // Must have at least 10 digits (US/CA format)
    // Must not be a short extension (like 109, 111, etc.)
    return (
      digits.length >= 10 && (digits.startsWith('1') || digits.length > 10)
    );
  };

  // Extract phone numbers from the API response payload
  const extractPhoneNumbers = (data) => {
    const phoneNumbers = new Set();

    data.forEach((item) => {
      try {
        if (
          item.event === 'phone.caller_ended' ||
          item.event === 'phone.callee_ended'
        ) {
          const payload = item.payload;
          if (payload?.object) {
            const object = payload.object;

            // Extract caller phone number if valid
            if (
              object.caller?.phone_number &&
              isValidUsCaNumber(object.caller.phone_number)
            ) {
              phoneNumbers.add(object.caller.phone_number);
            }

            // Extract callee phone number if valid and PSTN type
            if (
              object.callee?.phone_number &&
              object.callee.extension_type === 'pstn' &&
              isValidUsCaNumber(object.callee.phone_number)
            ) {
              phoneNumbers.add(object.callee.phone_number);
            }
          }
        }
      } catch (error) {
        console.error('Error processing phone number:', error);
      }
    });

    return Array.from(phoneNumbers).map((phone, index) => ({
      id: `phone-${index}`,
      phone,
      type: 'zoom phone',
    }));
  };

  // Fetch phone numbers from API
  const fetchPhoneNumbers = async () => {
    try {
      const url = `${apiUrl}/api/v1/lead/phone-numbers`;
      const res = await Get(url, accessToken);

      // Extract phone numbers from the response data
      return extractPhoneNumbers(res.data);
    } catch (err) {
      console.error('Failed to fetch phone numbers:', err);
      return [];
    }
  };

  // Fetch custom leads
  const fetchCustomLeads = async () => {
    try {
      const url = `${apiUrl}/api/v1/lead/custom`;
      const res = await Get(url, accessToken);
      return res.data.map((lead) => ({
        ...lead,
        id: lead._id,
        type: 'custom',
      }));
    } catch (err) {
      console.error('Failed to fetch custom leads:', err);
      return [];
    }
  };

  // Fetch existing contacts phones
  const fetchContactsPhones = async () => {
    try {
      const url = `${apiUrl}/api/v1/communication/s-contacts-conversion?limit=10000`;
      const res = await Get(url, accessToken, companyId);
      if (Array.isArray(res?.data?.result)) {
        // Filter only valid US/CA numbers
        const validPhones = res.data.result
          .map(
            (c) =>
              c.contact ||
              c.secondaryPhone ||
              c.alternativePhone ||
              c.cell ||
              '',
          )
          .filter((phone) => isValidUsCaNumber(phone));

        // console.log('validPhones--', validPhones);
        setContactsPhones(validPhones);
      }
    } catch (err) {
      setContactsPhones([]);
    }
  };

  // Refetch all data
  const refetchAllData = async () => {
    try {
      const [phoneNumbers, customLeadsData] = await Promise.all([
        fetchPhoneNumbers(),
        fetchCustomLeads(),
      ]);
      setLeadsData(phoneNumbers);
      setCustomLeads(customLeadsData);
    } catch (err) {
      console.error('Failed to refetch data:', err);
    }
  };

  // Initial data loading
  useEffect(() => {
    const fetchData = async () => {
      setTableLoading(true);
      try {
        await refetchAllData();
      } finally {
        setTableLoading(false);
      }
    };
    fetchData();
  }, [accessToken]);

  // Fetch contacts phones
  useEffect(() => {
    fetchContactsPhones();
  }, [accessToken, companyId]);

  // Filtering logic
  const isUpgradedLead = (lead) => {
    return (
      lead.firstName &&
      lead.lastName &&
      lead.role &&
      lead.firstName !== '-' &&
      lead.lastName !== '-' &&
      lead.role !== '-'
    );
  };

  const isPartiallyAddedLead = (lead) => {
    return (
      customLeads.some((customLead) => customLead.phone === lead.phone) &&
      !isUpgradedLead(lead)
    );
  };

  const isPhoneNumberOnlyLead = (lead) => {
    return (
      !lead.firstName &&
      !lead.lastName &&
      !lead.role &&
      !customLeads.some((customLead) => customLead.phone === lead.phone)
    );
  };

  // Filter phone numbers that haven't been added to leads yet
  const phoneNumbersTabLeads = [...leadsData, ...customLeads].filter(
    isPhoneNumberOnlyLead,
  );
  const filteredPhoneNumbersTabLeads = phoneNumbersTabLeads.filter(
    (lead) => !contactsPhones.includes(lead.phone),
  );

  useEffect(() => {
    if (typeof onPhoneCountUpdate === 'function') {
      onPhoneCountUpdate(filteredPhoneNumbersTabLeads.length);
    }
  }, [ filteredPhoneNumbersTabLeads.length, onPhoneCountUpdate ] );
  
  // Modal logic
  const handlePhoneEdit = (lead) => {
    const clearDash = (value) => {
      return value === '-' ? '' : value;
    };
    setPhoneEditData({
      oldPhone: lead.phone,
      newPhone: lead.phone,
      firstName: clearDash(lead.firstName || ''),
      lastName: clearDash(lead.lastName || ''),
      email: clearDash(lead.email || ''),
      address: clearDash(lead.address || ''),
      role: clearDash(lead.role || ''),
    });
    setShowPhoneEdit(true);
  };

  // Helper function to find existing contact by phone
  const findExistingContact = async (phone) => {
    try {
      const response = await Get(
        `${apiUrl}/api/v1/communication/s-contacts-conversion?search=${phone}&limit=1`,
        accessToken,
        companyId,
      );
      if (response?.data?.result && response.data.result.length > 0) {
        const contact = response.data.result.find(
          (c) => c.mobilePhone === phone || c.businessPhone === phone,
        );
        return contact;
      }
      return null;
    } catch (error) {
      console.error('Error finding existing contact:', error);
      return null;
    }
  };

  // Helper function to handle contact creation/update
  const handleContactSync = async (contactData) => {
    try {
      const existingContact = await findExistingContact(contactData.phone);
      if (existingContact) {
        const updateData = {
          firstName: contactData.firstName,
          lastName: contactData.lastName,
          email: contactData.email,
          role: contactData.role,
        };
        const url = `${apiUrl}/api/v1/users/admin/update-user`;
        await Patch(url, updateData, accessToken, companyId);
      } else {
        const contactBody = {
          firstName: contactData.firstName,
          lastName: contactData.lastName,
          contact: contactData.phone,
          email: contactData.email,
          role: contactData.role,
        };
        const url = `${apiUrl}/api/v1/communication/contact-regist`;
        await Post(url, contactBody, accessToken, companyId);
      }
    } catch (contactErr) {
      console.error('Contact sync failed:', contactErr);
    }
  };

  const handleZoomEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const leadType = zoomEditData.leadType;
      const endpoint = leadType === 'outlook' ? 'outlook-email' : 'zoom-email';

      // Prepare contact data
      const contactData = {
        firstName: zoomEditData.firstName,
        lastName: zoomEditData.lastName,
        contact: zoomEditData.phone
          ? `+1${zoomEditData.phone.replace(/\D/g, '')}`
          : '',
        email: zoomEditData.newEmail,
        role: zoomEditData.role,
      };

      await Post(
        `${apiUrl}/api/v1/lead`,
        {
          firstName: '-',
          lastName: '-',
          email: '-',
          phone: lead.phone,
          role: '-',
          __v: 0,
        },
        accessToken,
      );

      // Create/update contact in ContactManagement
      await handleContactSync(contactData);

      await refetchAllLeads();
      setShowZoomEdit(false);
    } catch (err) {
      console.error('Zoom/Outlook email upgrade failed:', err);
    }
  };

  const handlePhoneEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const contactData = {
        firstName: phoneEditData.firstName,
        lastName: phoneEditData.lastName,
        email: phoneEditData.email,
        phone: phoneEditData.newPhone,
        role: phoneEditData.role,
      };

      // Update the lead with full information
      await Post(
        `${apiUrl}/api/v1/lead`,
        {
          firstName: '-',
          lastName: '-',
          email: '-',
          phone: lead.phone,
          role: '-',
          __v: 0,
        },
        accessToken,
      );

      // Create/update contact in ContactManagement
      // await handleContactSync(contactData);
      await refetchAllData();
      setShowPhoneEdit(false);
    } catch (err) {
      console.error('Phone number upgrade failed:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPhoneEditData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddToLeads = async (lead) => {
    try {
      setActionLoading((prev) => new Set(prev).add(lead.phone));
      await Post(
        `${apiUrl}/api/v1/lead`,
        {
          firstName: '-',
          lastName: '-',
          email: '-',
          phone: lead.phone,
          role: '-',
          __v: 0,
        },
        accessToken,
      );
      await refetchAllData();
    } catch (err) {
      console.error('Failed to add lead:', err);
    } finally {
      setActionLoading((prev) => {
        const newSet = new Set(prev);
        newSet.delete(lead.phone);
        return newSet;
      });
    }
  };

  const handleDeletePhone = async (lead) => {
    try {
      setActionLoading((prev) => new Set(prev).add(lead.phone));
      const encoded = encodeURIComponent(lead.phone);
      await DeleteReq(
        `${apiUrl}/api/v1/lead/phone-numbers/${encoded}`,
        null,
        { headers: { Authorization: `Bearer ${accessToken}` } },
        companyId,
      );
      // Refresh local data
      await refetchAllData();
    } catch (err) {
      console.error('Failed to delete phone:', err);
    } finally {
      setActionLoading((prev) => {
        const newSet = new Set(prev);
        newSet.delete(lead.phone);
        return newSet;
      });
    }
  };

  return (
    <>
      <style>{`
        html, body { overflow-x: hidden !important; }
        body { overflow-y: hidden !important; }
        .table-scrollable { overflow-y: auto !important; max-height: 60vh !important; }
      `}</style>
      <section className="">
        <LeadsTable
          data={filteredPhoneNumbersTabLeads}
          loading={tableLoading}
          emptyMessage="No valid US/CA phone numbers found"
          skeletonRows={16}
          skeletonCols={3}
          columns={[
            {
              header: 'Source',
              key: 'type',
              width: '35%',
              align: 'left',
            },
            {
              header: 'Phone Number',
              key: 'phone',
              width: '50%',
              align: 'left',
            },
            {
              header: 'Actions',
              width: '15%',
              align: 'center',
              render: (lead) => (
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                  }}
                >
                  {actionLoading.has(lead.phone) ? (
                    <CircularProgress size={20} style={{ color: '#0072C6' }} />
                  ) : (
                    <>
                      <button
                        onClick={() => handleAddToLeads(lead)}
                        className="leads-edit-btn"
                        disabled={actionLoading.has(lead.phone)}
                        style={{ textWrap: 'nowrap' }}
                      >
                        Add to Leads
                      </button>
                      <button
                        onClick={() => handleDeletePhone(lead)}
                        className="leads-edit-btn"
                        style={{ backgroundColor: '#dc3545' }}
                        disabled={actionLoading.has(lead.phone)}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              ),
            },
          ]}
        />
        <Dialog
          open={showPhoneEdit}
          onClose={() => setShowPhoneEdit(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'start',
              justifyContent: 'space-between',
            }}
          >
            <Typography component="h5" variant="h5">
              Upgrade Phone Number
            </Typography>
            <IconButton onClick={() => setShowPhoneEdit(false)}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <form
              id="phone-numbers-upgrade-form"
              onSubmit={handlePhoneEditSubmit}
            >
              <Box py={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="firstName"
                      label="First Name"
                      value={phoneEditData.firstName}
                      onChange={handleInputChange}
                      fullWidth
                      required
                      autoFocus
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="lastName"
                      label="Last Name"
                      value={phoneEditData.lastName}
                      onChange={handleInputChange}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="newPhone"
                      label="Phone Number"
                      value={phoneEditData.newPhone}
                      onChange={handleInputChange}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      type="email"
                      name="email"
                      label="Email"
                      value={phoneEditData.email}
                      onChange={handleInputChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      select
                      name="role"
                      label="Role"
                      value={phoneEditData.role}
                      onChange={handleInputChange}
                      fullWidth
                      required
                    >
                      <MenuItem value="">Select Role</MenuItem>
                      {rolesOptions.map((r) => (
                        <MenuItem key={r.value} value={r.value}>
                          {r.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </Box>
            </form>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              type="submit"
              form="phone-numbers-upgrade-form"
              variant="contained"
              sx={{
                backgroundColor: '#0072C6',
                fontFamily: "'Open Sans', sans-serif",
                fontWeight: '500',
                '&:hover': {
                  backgroundColor: '#005A9E',
                },
                padding: '10px 20px',
                fontSize: '16px',
              }}
            >
              Save
            </Button>
            <Button
              onClick={() => setShowPhoneEdit(false)}
              variant="contained"
              sx={{
                backgroundColor: '#dc3545',
                fontFamily: "'Open Sans', sans-serif",
                fontWeight: '500',
                '&:hover': {
                  backgroundColor: '#a71d2a',
                },
                padding: '10px 20px',
                fontSize: '16px',
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </section>
    </>
  );
}
